import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Gem } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Form } from '@/components/ui/form'
import SettingField from '../../../../components/form-fields/SettingField'
import { zSetting } from '../../../../helpers/forms'
import { FormEntityContext } from '../../../../config/context/forms'
import { useTeam } from '../../../../config/context/team'
import { useTeamDroptimizerConfigurations } from '../../../../config/queries'
import { updateTeam } from '../../../../api/team'
import { displayErrorToast } from '../../../../helpers'
import DroptimizerConfigurationFormContent, {
  EditableDroptimizerConfiguration,
} from '../../../TeamDashboard/subpages/TeamSettings/components/DroptimizerConfigurationFormContent'
import RcLootCouncilCta from '../../../TeamDashboard/subpages/TeamLoot/components/RcLootCouncilCta'
import StepLayout from '../StepLayout'
import StepFooter from '../StepFooter'
import { StepProps } from '../types'

const formSchema = z.object({
  settings: z.object({
    wishlistDifficulties: zSetting(z.array(z.string())),
    wishlistAutoClear: zSetting(z.coerce.number().min(0).max(720)),
  }),
  droptimizer_configurations_attributes: z.array(
    z.object({
      id: z.number().optional(),
      name: z.string(),
      fightStyle: z.string(),
      sockets: z.boolean(),
      pi: z.boolean(),
      matchEquippedGear: z.boolean(),
      numberOfBossesOperator: z.enum([
        'smaller',
        'smaller_or_equal',
        'equal',
        'greater_or_equal',
        'greater',
      ]),
      numberOfBosses: z.number(),
      fightDurationOperator: z.enum([
        'smaller',
        'smaller_or_equal',
        'equal',
        'greater_or_equal',
        'greater',
      ]),
      fightDuration: z.number(),
      weight: z.number(),
      upgradeLevel: z.number(),
      upgradeLevelHeroic: z.number(),
      upgradeLevelNormal: z.number(),
      upgradeLevelLfr: z.number(),
      expertMode: z.boolean(),
      voidforged: z.boolean(),
      _destroy: z.boolean().optional(),
    }),
  ),
})

type LootFormValues = z.infer<typeof formSchema>

interface LootStepFormProps extends StepProps {
  droptimizerConfigurations: EditableDroptimizerConfiguration[]
}

const LootStepForm = ({
  goNext,
  goBack,
  isFirst,
  droptimizerConfigurations,
}: LootStepFormProps) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<LootFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      settings: {
        wishlistDifficulties: team.settings.wishlistDifficulties,
        wishlistAutoClear: team.settings.wishlistAutoClear,
      },
      droptimizer_configurations_attributes:
        droptimizerConfigurations as LootFormValues['droptimizer_configurations_attributes'],
    },
  })

  const onSubmit = (values: LootFormValues) => {
    setSubmitting(true)

    updateTeam(team.id, {
      settings: values.settings,
      droptimizer_configurations_attributes:
        values.droptimizer_configurations_attributes,
    } as unknown as Partial<wowaudit.Team>)
      .then((updatedTeam) => {
        queryClient.setQueryData(['team', { path: team.path }], updatedTeam)
        queryClient.invalidateQueries({
          queryKey: ['teamDroptimizerConfigurations', { id: team.id }],
        })
        setSubmitting(false)
        goNext()
      })
      .catch((error) => {
        displayErrorToast(error, t)
        setSubmitting(false)
      })
  }

  return (
    <StepLayout
      icon={Gem}
      title='Loot wishlists & history'
      description='Let your team upload sim reports from Raidbots and Questionably Epic, giving a clear overview of all upgrades.'
    >
      <Form {...form}>
        <FormEntityContext.Provider
          value={{ entityType: 'team', i18nPrefix: 'forms.fields' }}
        >
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <SettingField name='wishlistDifficulties' />
            <SettingField name='wishlistAutoClear' layout='box' />

            <h5 className='mt-6'>Droptimizer configurations</h5>
            <p className='mb-4'>
              Members upload their sim reports for each configuration, and the
              weighted upgrade values are shown in the wishlist overview. Set up
              multiple configurations, for example single target and AoE, to
              combine their results.
            </p>

            <DroptimizerConfigurationFormContent />

            <div className='mt-6'>
              <RcLootCouncilCta dismissible={false} />
            </div>

            <StepFooter
              showBack={!isFirst}
              onBack={goBack}
              onContinue={form.handleSubmit(onSubmit)}
              continueLoading={submitting}
              onSkip={goNext}
            />
          </form>
        </FormEntityContext.Provider>
      </Form>
    </StepLayout>
  )
}

const LootStep = (props: StepProps) => {
  const { team } = useTeam()
  const { data: droptimizerConfigurations, isPending } =
    useTeamDroptimizerConfigurations(team.id)

  if (isPending) return null

  return (
    <LootStepForm
      {...props}
      droptimizerConfigurations={droptimizerConfigurations ?? []}
    />
  )
}

export default LootStep
